import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useEffect, useState } from "react";
import { showDangerToast, showToast } from "../../constants/toaster";
import { useDispatch, useSelector } from "react-redux";
import { updateLoading } from "../../../redux/features/common/commonSlice";
import urls from "../../constants/apiurl";
import ApiCaller from "../../constants/ApiCaller";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";
import "../../../assets/css/CustomDropdown.css";

// Custom CSS for fullscreen modal
const fullscreenModalStyles = `
    .fullscreen-modal .modal-dialog {
        max-width: none !important;
        margin: 0 !important;
    }
    
    .fullscreen-modal .modal-content {
        height: 100vh !important;
        border-radius: 0 !important;
        border: none !important;
    }
    
    .fullscreen-modal .modal-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #dee2e6;
    }
    
    .fullscreen-modal .modal-header .btn-close {
        filter: brightness(0) invert(1);
    }
    
    .fullscreen-modal .modal-body {
        flex: 1;
        overflow-y: auto;
        padding: 0;
        background: #f8f9fa;
    }
    
    .fullscreen-modal .modal-footer {
        background: white;
        box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
    }
    
    .fullscreen-modal .card {
        border: none;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    
    .fullscreen-modal .card:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }
`;

export const AddMerchantSubscriptionPlan = (props) => {
  const userData = useSelector((state) => state?.userData);
  const dispatch = useDispatch();

  // State for dropdowns
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [apiLoading, setApiLoading] = useState({
    categories: false,
    partners: false,
  });

  // State for form data
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    amount: 0,
    viewerSubscription: false,
    merchantSubscriptionCategoryId: "",
    type: "DAILY",
    benefits: [],
    gifts: [],
    viewerNetworkMapping: [],
  });

  // State for adding benefits
  const [newBenefit, setNewBenefit] = useState({
    benefitsType: "",
    value: "",
  });

  // State for gift configuration
  const [newGift, setNewGift] = useState({
    type: "",
    thresholdValue: "",
    instantGift: "",
    accumulatedGift: "",
  });

  useEffect(() => {
    console.log("Modal effect triggered. showModal:", props?.showModal);
    console.log("User data available:", !!userData?.access_token);

    if (props?.showModal && userData?.access_token) {
      console.log("Starting API call for categories...");
      fetchCategories();
      fetchPartners();

      // Initialize form data from props
      if (props?.formData) {
        console.log("Setting form data from props:", props.formData);

        // Transform benefits to include unique IDs
        const transformedBenefits = (props.formData.benefits || []).map(benefit => ({
          ...benefit,
          id: benefit.id || uuidv4() // Add ID if not present
        }));

        // Transform gifts to include unique IDs and map API response property names
        const transformedGifts = (props.formData.gifts || []).map(gift => ({
          id: gift.id || uuidv4(), // Add ID if not present
          type: gift.type,
          thresholdValue: gift.thresholdValue,
          instanceValue: gift.instantGift || gift.instanceValue, // Map API response
          accumulatedValue: gift.accumulatedGift || gift.accumulatedValue // Map API response
        }));
        console.log("props.formData:", props.formData);
        console.log("viewerNetworkMapping from props:", props.formData.viewerNetworkMapping);
        setFormData({
          ...props.formData,
          benefits: transformedBenefits,
          gifts: transformedGifts,
          viewerNetworkMapping: props.formData.viewerNetworkMapping || [],
        });
      } else {
        console.log("Resetting form data to defaults");
        setFormData({
          id: "",
          name: "",
          amount: 0,
          merchantSubscriptionCategoryId: "",
          type: "DAILY",
          benefits: [],
          gifts: [],
          viewerNetworkMapping: [],
        });
      }
    }
  }, [props?.showModal, userData?.access_token]);

  const fetchCategories = async () => {
    try {
      setApiLoading((prev) => ({ ...prev, categories: true }));
      console.log(
        "Fetching categories from:",
        urls.merchantSubscriptionCategories
      );
      console.log(
        "With access token:",
        userData?.access_token ? "Present" : "Missing"
      );

      const res = await ApiCaller.get(
        {},
        urls.merchantSubscriptionCategories,
        userData?.access_token
      );
      console.log("Raw response:", res);

      const response = await res.response.json();
      console.log("Categories response:", response);

      if (response.status === 200 || response.status === "200") {
        setCategories(response.data || []);
        console.log("Categories loaded:", response.data?.length, "items");
      } else {
        console.error("Categories fetch failed:", response.message);
        showDangerToast(response.message || "Failed to fetch categories");
        setCategories([]); // Set empty array on failure
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showDangerToast("Failed to load categories");
      setCategories([]); // Set empty array on error
    } finally {
      setApiLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  const fetchPartners = async () => {
    try {
      setApiLoading((prev) => ({ ...prev, partners: true }));
      console.log(
        "Fetching partners from:",
        urls.merchantSubscriptionPartners
      );

      const res = await ApiCaller.get(
        {},
        urls.merchantSubscriptionPartners,
        userData?.access_token
      );
      console.log("Raw response:", res);

      const response = await res.response.json();
      console.log("Partners response:", response);

      if (response.status === 200 || response.status === "200") {
        setPartners(response.data || []);
        console.log("Partners loaded:", response.data?.length, "items");
      } else {
        console.error("Partners fetch failed:", response.message);
        showDangerToast(response.message || "Failed to fetch partners");
        setPartners([]);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
      showDangerToast("Failed to load partners");
      setPartners([]);
    } finally {
      setApiLoading((prev) => ({ ...prev, partners: false }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addBenefit = () => {
    if (!newBenefit.benefitsType || !newBenefit.value) {
      showDangerToast("Please fill all benefit fields");
      return;
    }

    // Check if benefit type already exists
    const existingBenefits = formData.benefits || [];
    const benefitTypeExists = existingBenefits.some(
      (benefit) => benefit.benefitsType === newBenefit.benefitsType
    );

    if (benefitTypeExists) {
      showDangerToast(
        `${newBenefit.benefitsType.replace(
          "_",
          " "
        )} benefit is already configured. Each benefit type can only be added once.`
      );
      return;
    }

    const benefit = {
      id: uuidv4(),
      benefitsType: newBenefit.benefitsType,
      value: newBenefit.value,
    };

    setFormData((prev) => ({
      ...prev,
      benefits: [...(prev.benefits || []), benefit],
    }));

    setNewBenefit({
      benefitsType: "",
      value: "",
    });
  };

  const removeBenefit = (benefitId) => {
    setFormData((prev) => ({
      ...prev,
      benefits: (prev.benefits || []).filter((b) => b.id !== benefitId),
    }));
  };

  const addGift = () => {
    if (
      !newGift.type ||
      !newGift.thresholdValue ||
      !newGift.instantGift ||
      !newGift.accumulatedGift
    ) {
      showDangerToast("Please fill all gift configuration fields");
      return;
    }

    // Check if gift type already exists
    const existingGifts = formData.gifts || [];
    const giftTypeExists = existingGifts.some(
      (gift) => gift.type === newGift.type
    );

    if (giftTypeExists) {
      showDangerToast(
        `${newGift.type.replace(
          "_",
          " "
        )} is already configured. Each gift type can only be added once.`
      );
      return;
    }

    const gift = {
      id: uuidv4(),
      type: newGift.type,
      thresholdValue: newGift.thresholdValue,
      instantGift: newGift.instantGift,
      accumulatedGift: newGift.accumulatedGift,
    };

    setFormData((prev) => ({
      ...prev,
      gifts: [...(prev.gifts || []), gift],
    }));

    setNewGift({
      type: "",
      thresholdValue: "",
      instantGift: "",
      accumulatedGift: "",
    });
  };

  const removeGift = (giftId) => {
    setFormData((prev) => ({
      ...prev,
      gifts: (prev.gifts || []).filter((g) => g.id !== giftId),
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name?.trim()) {
      showDangerToast("Plan name is required");
      return;
    }
    if (!formData.amount || formData.amount <= 0) {
      showDangerToast("Amount must be greater than 0");
      return;
    }
    if (!formData.merchantSubscriptionCategoryId) {
      showDangerToast("Please select a category");
      return;
    }
    try {
      dispatch(updateLoading(true));
      const postJson = {
        id: formData.id || "",
        name: formData.name.trim(),
        amount: formData.amount.toString(),
        viewerSubscription: formData.viewerSubscription,
        merchantSubscriptionCategoryId: formData.merchantSubscriptionCategoryId,
        type: formData.type,
        benefits: (formData.benefits || []).map((b) => ({
          benefitsType: b.benefitsType,
          value: b.value,
        })),
        gifts: (formData.gifts || []).map((g) => ({
          type: g.type,
          thresholdValue: g.thresholdValue,
          instantGift: g.instantGift,
          accumulatedGift: g.accumulatedGift,
        })),
        viewerNetworkMapping: formData.viewerNetworkMapping || [],
      };
      console.log("Submitting merchant subscription plan:", postJson);
      const path = urls.addMerchantSubscriptionPlan;
      const res = await ApiCaller.post(postJson, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === "200") {
        showToast(response?.message || "Subscription plan saved successfully");
        handleClose();
        props.setCallAPi(uuidv4());
      } else {
        showDangerToast(response.message || "Failed to save subscription plan");
      }
    } catch (error) {
      console.error("Error saving subscription plan:", error);
      showDangerToast("Failed to save subscription plan");
    } finally {
      dispatch(updateLoading(false));
    }
  };

  const handleClose = () => {
    setFormData({
      id: "",
      name: "",
      amount: 0,
      viewerSubscription: false,
      merchantSubscriptionCategoryId: "",
      type: "DAILY",
      benefits: [],
      gifts: [],
      viewerNetworkMapping: [],
    });
    setNewBenefit({
      benefitsType: "",
      value: "",
    });
    setNewGift({
      type: "",
      thresholdValue: "",
      instantGift: "",
      accumulatedGift: "",
    });
    props.setShowModal(false);
  };

  // Inject custom styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = fullscreenModalStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <Modal
      show={props.showModal}
      onHide={handleClose}
      fullscreen={true}
      backdrop="static"
      className="fullscreen-modal"
    >
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="h4">
          {formData.id ? "Edit" : "Add"} {formData.viewerSubscription ? "Viewer" : "Merchant"} Subscription Plan
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="p-0"
        style={{ height: "calc(100vh - 120px)", overflowY: "auto" }}
      >
        <div className="container-fluid p-3">
          {/* Basic Information Section */}
          <div className="card mb-5 shadow-sm">
            <div className="card-header bg-primary text-white py-3">
              <h5 className="mb-0 fw-bold">📋 Basic Information</h5>
            </div>
            <div className="card-body p-5">
              <h6 className="text-primary mb-3 fw-bold">
                📋 Basic Information
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Plan Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter plan name"
                      maxLength={100}
                      style={{ borderRadius: "8px", border: "1px solid #ddd" }}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-2">
                    <label className="form-label fw-semibold">
                      Amount <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={formData.amount}
                      onChange={(e) =>
                        handleInputChange("amount", e.target.value)
                      }
                      placeholder="Enter amount"
                      style={{ borderRadius: "8px", border: "1px solid #ddd" }}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="form-label fw-semibold">
                      Duration Type <span className="text-danger">*</span>
                    </label>
                    <div className="custom-dropdown">
                      <select
                        className="form-select"
                        value={formData.type}
                        onChange={(e) =>
                          handleInputChange("type", e.target.value)
                        }
                      >
                        <option value="DAILY">Daily</option>
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="YEARLY">Yearly</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="form-label fw-semibold">
                      Subscription Type <span className="text-danger">*</span>
                    </label>
                    <div className="btn-group w-100" role="group" style={{ borderRadius: "8px", overflow: "hidden" }}>
                      <button
                        type="button"
                        className={`btn ${!formData.viewerSubscription ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleInputChange("viewerSubscription", false)}
                        style={{
                          borderRadius: "8px 0 0 8px",
                          fontWeight: !formData.viewerSubscription ? "600" : "normal"
                        }}
                      >
                        Merchant
                      </button>
                      <button
                        type="button"
                        className={`btn ${formData.viewerSubscription ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleInputChange("viewerSubscription", true)}
                        style={{
                          borderRadius: "0 8px 8px 0",
                          fontWeight: formData.viewerSubscription ? "600" : "normal"
                        }}
                      >
                        Viewer
                      </button>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-2">
                    <label className="form-label fw-semibold">
                      Category <span className="text-danger">*</span>
                      {categories?.length > 0 && (
                        <span className="badge bg-success ms-2 fs-6">
                          {categories?.length}
                        </span>
                      )}
                    </label>
                    <div
                      className={`custom-dropdown ${apiLoading.categories ? "loading" : ""
                        }`}
                    >
                      <select
                        className="form-select"
                        value={formData.merchantSubscriptionCategoryId}
                        onChange={(e) =>
                          handleInputChange(
                            "merchantSubscriptionCategoryId",
                            e.target.value
                          )
                        }
                        disabled={apiLoading.categories}
                      >
                        <option value="">
                          {apiLoading.categories
                            ? "Loading categories..."
                            : "Select Category"}
                        </option>
                        {categories.map((category) => (
                          <option key={category.key} value={category.key}>
                            {category.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="mb-0">
                    <label className="form-label fw-semibold">
                      Network Partners
                      {partners?.length > 0 && (
                        <span className="badge bg-success ms-2 fs-6">
                          {partners?.length}
                        </span>
                      )}
                    </label>
                    {apiLoading.partners ? (
                      <div className="form-control" style={{ borderRadius: "8px", border: "1px solid #ddd", padding: "0.375rem 0.75rem" }}>
                        Loading partners...
                      </div>
                    ) : (
                      <Select
                        isMulti
                        options={partners.map(partner => ({
                          value: partner.key,
                          label: partner.value
                        }))}
                        value={
                          (formData.viewerNetworkMapping || [])
                            .map(mappingKey => {
                              const partner = partners.find(p => p.key === mappingKey);
                              return partner ? { value: partner.key, label: partner.value } : null;
                            })
                            .filter(Boolean)
                        }
                        onChange={(selectedOptions) => {
                          const selectedKeys = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                          handleInputChange("viewerNetworkMapping", selectedKeys);
                        }}
                        placeholder="Select Network Partners..."
                        isDisabled={apiLoading.partners}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                            minHeight: "38px"
                          })
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Benefits Section */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-info text-white py-2 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">📦 Subscription Benefits</h6>
                {/* <div className="d-flex align-items-center">
                  <span className="badge bg-white text-dark me-2">
                    {(formData.benefits || []).length}/4 Types
                  </span>
                  {(formData.benefits || []).length >= 4 && (
                    <span className="badge bg-success">✅ Complete</span>
                  )}
                </div> */}
              </div>
              <div className="card-body p-3">
                <div className="row g-2 mb-2">
                  <div className="col-md-5">
                    <div className="custom-dropdown dropdown-sm">
                      <select
                        className="form-select form-select-sm"
                        value={newBenefit.benefitsType}
                        onChange={(e) =>
                          setNewBenefit((prev) => ({
                            ...prev,
                            benefitsType: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Benefit Type</option>
                        {!formData?.viewerSubscription && (
                          <>
                            <option
                              value="PRODUCT_CATALOG"
                              disabled={(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "PRODUCT_CATALOG"
                              )}
                            >
                              Product Catalog{" "}
                              {(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "PRODUCT_CATALOG"
                              )
                                ? "(Already Added)"
                                : ""}
                            </option>
                            <option
                              value="PRODUCT_SPECIFIC_REEL"
                              disabled={(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "PRODUCT_SPECIFIC_REEL"
                              )}
                            >
                              Product Specific Reel{" "}
                              {(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "PRODUCT_SPECIFIC_REEL"
                              )
                                ? "(Already Added)"
                                : ""}
                            </option>
                            <option
                              value="BOOSTING_IMPRESSION"
                              disabled={(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "BOOSTING_IMPRESSION"
                              )}
                            >
                              Boosting Impression{" "}
                              {(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "BOOSTING_IMPRESSION"
                              )
                                ? "(Already Added)"
                                : ""}
                            </option>
                            <option
                              value="ONE_TO_MANY"
                              disabled={(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "ONE_TO_MANY"
                              )}
                            >
                              One To Many{" "}
                              {(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "ONE_TO_MANY"
                              )
                                ? "(Already Added)"
                                : ""}
                            </option>
                          </>
                        )}
                        {formData?.viewerSubscription && (
                          <>
                            <option
                              value="WATCH_TIME"
                              disabled={(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "WATCH_TIME"
                              )}
                            >
                              Watch Time{" "}
                              {(formData.benefits || []).some(
                                (benefit) => benefit.benefitsType === "WATCH_TIME"
                              )
                                ? "(Already Added)"
                                : ""}
                            </option>
                          </>
                        )}

                      </select>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-sm">
                      Value
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      value={newBenefit.value}
                      onChange={(e) =>
                        setNewBenefit((prev) => ({
                          ...prev,
                          value: e.target.value,
                        }))
                      }
                      placeholder="Enter value"
                      style={{
                        borderRadius: "6px",
                        fontSize: "14px",
                        padding: "0.375rem 0.75rem",
                        border: "1px solid #ced4da",
                        backgroundColor: "white",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        transition:
                          "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                      }}
                    />
                  </div>

                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      type="button"
                      className={`btn btn-sm w-100 ${(formData.benefits || []).length >= 4
                        ? "btn-secondary"
                        : "btn-info"
                        }`}
                      onClick={addBenefit}
                      disabled={
                        !newBenefit.benefitsType ||
                        !newBenefit.value ||
                        (formData.benefits || []).length >= 4
                      }
                      style={{ borderRadius: "6px", fontSize: "14px" }}
                      title={
                        (formData.benefits || []).length >= 4
                          ? "Maximum benefit types reached (4/4)"
                          : "Add Subscription Benefit"
                      }
                    >
                      {(formData.benefits || []).length >= 4
                        ? "🔒 Max Reached"
                        : "➕ Add Benefit"}
                    </button>
                  </div>
                </div>

                {/* Selected Benefits List */}
                {formData.benefits?.length > 0 && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-info me-2">
                        {formData.benefits?.length}
                      </span>
                      <small className="fw-semibold">
                        Selected Benefits
                      </small>
                    </div>
                    <div
                      className="border rounded p-2"
                      style={{
                        maxHeight: "150px",
                        overflowY: "auto",
                        backgroundColor: "white",
                      }}
                    >
                      {formData.benefits.map((benefit) => (
                        <div
                          key={benefit.id}
                          className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                          style={{ backgroundColor: "#e1f5fe" }}
                        >
                          <div className="flex-grow-1">
                            <div className="fw-bold text-sm">
                              {benefit.benefitsType}
                            </div>
                            <small className="text-muted">
                              Value: {benefit.value}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeBenefit(benefit.id)}
                            style={{ fontSize: "12px" }}
                          >
                            ❌
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Gift Configuration Section */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-warning text-dark py-2 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">🎁 Gift Configuration</h6>
                <div className="d-flex align-items-center">
                  <span className="badge bg-white me-2">
                    {(formData.gifts || []).length}/2 Types
                  </span>
                  {(formData.gifts || []).length >= 2 && (
                    <span className="badge bg-success">✅ Complete</span>
                  )}
                </div>
              </div>
              <div className="card-body p-3">
                <div className="row g-2 mb-2">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Gift Type</label>
                    <div className="custom-dropdown dropdown-sm">
                      <select
                        className="form-select form-select-sm"
                        value={newGift.type}
                        onChange={(e) =>
                          setNewGift((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Gift Type</option>
                        <option
                          value="DATA_GIFT"
                          disabled={(formData.gifts || []).some(
                            (gift) => gift.type === "DATA_GIFT"
                          )}
                        >
                          Data Gift{" "}
                          {(formData.gifts || []).some(
                            (gift) => gift.type === "DATA_GIFT"
                          )
                            ? "(Already Added)"
                            : ""}
                        </option>
                        <option
                          value="BEADS"
                          disabled={(formData.gifts || []).some(
                            (gift) => gift.type === "BEADS"
                          )}
                        >
                          Beads{" "}
                          {(formData.gifts || []).some(
                            (gift) => gift.type === "BEADS"
                          )
                            ? "(Already Added)"
                            : ""}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">
                      Threshold Value(days)
                    </label>
                    <div className="input-group input-group-sm">
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={newGift.thresholdValue}
                        onChange={(e) =>
                          setNewGift((prev) => ({
                            ...prev,
                            thresholdValue: e.target.value,
                          }))
                        }
                        style={{
                          borderRadius: "6px 0 0 6px",
                          fontSize: "14px",
                          border: "1px solid #ced4da",
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label fw-semibold">
                      Instant Gift
                    </label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newGift.instantGift}
                        onChange={(e) =>
                          setNewGift((prev) => ({
                            ...prev,
                            instantGift: e.target.value,
                          }))
                        }
                        placeholder={
                          newGift.type === "DATA_GIFT" ? "10MB" : newGift.type === "BEADS" ? "10 Beads" : "Enter value"
                        }
                        style={{
                          borderRadius: "6px",
                          fontSize: "14px",
                          border: "1px solid #ced4da",
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label fw-semibold">
                      Accumulated Gift
                    </label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newGift.accumulatedGift}
                        onChange={(e) =>
                          setNewGift((prev) => ({
                            ...prev,
                            accumulatedGift: e.target.value,
                          }))
                        }
                        placeholder={
                          newGift.type === "DATA_GIFT" ? "35MB" : newGift.type === "BEADS" ? "50 Beads" : "Enter value"
                        }
                        style={{
                          borderRadius: "6px",
                          fontSize: "14px",
                          border: "1px solid #ced4da",
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      type="button"
                      className={`btn btn-sm w-100 ${(formData.gifts || []).length >= 2
                        ? "btn-secondary"
                        : "btn-warning"
                        }`}
                      onClick={addGift}
                      disabled={
                        !newGift.type ||
                        !newGift.thresholdValue ||
                        !newGift.instantGift ||
                        !newGift.accumulatedGift ||
                        (formData.gifts || []).length >= 2
                      }
                      style={{ borderRadius: "6px", fontSize: "14px" }}
                      title={
                        (formData.gifts || []).length >= 2
                          ? "Maximum gift types reached (2/2)"
                          : "Add Gift Configuration"
                      }
                    >
                      {(formData.gifts || []).length >= 2
                        ? "🔒 Max Reached"
                        : "🎁 Add Gift"}
                    </button>
                  </div>
                </div>
                {/* Selected Gifts List */}
                {formData?.gifts?.length > 0 && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-warning text-dark me-2">
                        {formData.gifts?.length}
                      </span>
                      <strong className="fw-semibold">Configured Gifts</strong>
                    </div>
                    <div
                      className="border rounded p-2"
                      style={{
                        maxHeight: "150px",
                        overflowY: "auto",
                        backgroundColor: "white",
                      }}
                    >
                      {formData.gifts.map((gift) => (
                        <div
                          key={gift.id}
                          className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                          style={{ backgroundColor: "#fff3cd" }}
                        >
                          <div className="flex-grow-1">
                            <div className="row">
                              <div className="col-md-3">
                                <div
                                  className="fw-bold text-warning"
                                  style={{ fontSize: "14px" }}
                                >
                                  🎁 {gift.type.replace("_", " ")}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  Threshold
                                </small>
                                <strong>{gift.thresholdValue} days</strong>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  Instant Gift
                                </small>
                                <strong>
                                  {gift.instanceValue}{gift.type === "DATA_GIFT" ? "MB" : "Beads"}
                                </strong>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted d-block">
                                  Accumulated Gift
                                </small>
                                <strong>
                                  {gift.accumulatedValue}{" "}
                                  {gift.type === "DATA_GIFT" ? "MB" : "Beads"}
                                </strong>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm ms-3"
                            onClick={() => removeGift(gift.id)}
                            style={{ fontSize: "12px" }}
                          >
                            ❌
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top bg-light py-2 px-3 d-flex justify-content-between align-items-center">
        <div>
          <small className="text-muted">
            {categories?.length > 0 &&
              `${categories?.length} categories loaded`}
            {formData.benefits?.length > 0 && " • "}
            {formData.benefits?.length > 0 &&
              `${formData.benefits?.length} benefits configured`}
            {formData.gifts?.length > 0 && " • "}
            {formData.gifts?.length > 0 &&
              `${formData.gifts?.length} gifts configured`}
          </small>
        </div>
        <div>
          <Button variant="secondary" onClick={handleClose} className="me-2 btn-sm">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} className="btn-sm">
            {formData.id ? "Update" : "Create"} Plan
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
